/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {EMPTY, merge, Observable, of} from 'rxjs';
import {catchError, filter, finalize, map, mergeMap, share, switchMap, takeUntil} from 'rxjs/operators';

import {ESSService} from '../../../+ess/employee-self.service';
import {Destroyable} from '../../../destroyable.component';
import {ModalService} from '../../../shared/modal';
import {User, UserService} from '../../../shared/user';
import {isPresent} from '../../../shared/utils/lang';
import {LeaveAnswer, LeaveAnswerWorkflowV2, Notification} from '../../shared/communications.interface';
import {CommunicationsService} from '../../shared/communications.service';

import {LocalizationService} from '../../../shared/localization/localization.service';
import {NotificationsService} from '../notifications.service';

const moment = require('moment');

@Component({
  selector: 'gp-notification-details',
  templateUrl: './notification-details.component.html',
  styleUrls: [
    '../notifications-general.styles.scss',
    'notification-details.component.scss',
    '../notifications-list/notifications-list.component.scss'
  ]
})
export class NotificationDetailsComponent extends Destroyable implements OnInit {

  public notification: Notification;
  public buttons: string[] = [];
  public comment: string = '';
  public sending: boolean;
  public isSent: boolean = false;
  public selectedTeam: any;
  public isWorkflowV2: boolean = false;

  private user: User;
  public trigger$: Observable<number>;

  constructor(private userService: UserService,
              private localization: LocalizationService,
              private route: ActivatedRoute,
              private router: Router,
              private essService: ESSService,
              private translate: TranslateService,
              private modalService: ModalService,
              public communicationsService: CommunicationsService,
              public notificationsService: NotificationsService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public ngOnInit() {
    this.trigger$ = this.localization.changeDateFormatEvent.pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );
    this.userService.getUser()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(user => this.user = user);

    merge(
      this.route.params
        .pipe(
          map((params: any) => parseInt(params.id, 10) || undefined)
        ),
      this.translate.onLangChange
        .pipe(
          map(() => this.notification ? this.notification.id : null)
        )
    )
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter((id: number) => isPresent(id)),
        switchMap((id: number) =>
          this.notificationsService.getNotification(id).pipe(
            catchError(() => {
              this.router.navigate(['/inbox/notifications']);
              return EMPTY;
            }),
            mergeMap((notification: Notification) => {
              this.isSent = false;
              this.notification = notification;
              if (!this.isDestroyed) {
                this.cd.detectChanges();
              }
              if (this.notification.notificationBodyArgs.isWorkflowV2) {
                this.isWorkflowV2 = true;
              }

              this.buttons = Object.keys(this.notification.notificationBodyArgs)
                .filter((key: string) => key.includes('Button:'))
                .map((key: string) => <string>this.notification.notificationBodyArgs[key]);

              if (notification && !notification.read) {
                return this.notificationsService.updateNotificationsStatusByIds([notification.id], true);
              }

              return of(true);
            }))
        )).subscribe();
  }

  public sendAnswer(managerOutput: string) {
    if (this.isWorkflowV2) {
      const taskId = <string>this.notification.notificationBodyArgs.taskId;
      const taskDefinitionKey = <string>this.notification.notificationBodyArgs.taskDefinitionKey;
      const variables: LeaveAnswerWorkflowV2 = {
        managerOutput,
        managerComment: this.comment.trim()
      };

      this.sending = true;
      this.essService.sendLeaveAnswerWorkflowV2(taskId, taskDefinitionKey, variables, this.notification.id, managerOutput)
        .pipe(
          finalize(() => this.sending = false),
          switchMap(() => this.notificationsService.getNotification(this.notification.id)),
          map(notification => this.notification = notification)
        )
        .subscribe(() => this.isSent = true);
    } else {
      const data: LeaveAnswer = {
        userNotificationId: this.notification.id,
        managerFirstName: this.user.firstName,
        managerLastName: this.user.lastName,
        managerOutput,
        managerComment: this.comment.trim(),
        workflowId: <number>this.notification.notificationBodyArgs.workflowId,
        actionRepWorkflowId: <number>this.notification.notificationBodyArgs.actionRepWorkflowId,
        issuerContractId: <number>this.notification.notificationBodyArgs.issuerContractId,
        associatedWorkflowIds: <string>this.notification.notificationBodyArgs.associatedWorkflowIds
      };

      this.sending = true;
      this.essService.sendLeaveAnswer(data)
        .pipe(
          finalize(() => this.sending = false),
          switchMap(() => this.notificationsService.getNotification(this.notification.id)),
          map(notification => this.notification = notification)
        )
        .subscribe(() => this.isSent = true);
    }
  }

  public onNotificationBodyClick(event) {
    const dataFile = event.target.dataset.file;
    if (dataFile) {
      event.preventDefault();
      const path = event.target.dataset.path;
      if (path) {
        const fileName = event.target.dataset.fileName;
        this.notificationsService.getFile(dataFile, fileName, path)
          .subscribe();
      } else {
        this.notificationsService.getFile(dataFile, event.target.innerHTML)
          .subscribe();
      }
    }

    const dataTeamPlanning = event.target.dataset.teamPlanning;
    if (dataTeamPlanning) {
      const teamId: number | string = dataTeamPlanning;

      event.preventDefault();

      this.selectedTeam = {
        id: teamId === 'Default' ? 0 : teamId,
        currentDate: moment(this.notification.notificationBodyArgs)
      };

      this.modalService.openModalWithId('team-planning');
    }
  }

  public onModalClosed() {
    this.selectedTeam = null;
  }
}
